import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const appName = process.env.REACT_APP_NAME;
let brandLogo = null;
try {
  brandLogo = require(`@/assets/images/${appName}/horizontal-logo.svg`);
} catch (e) {
  brandLogo = null;
}
import { Formik, Form } from 'formik';
import { selectionProcessesPublic } from '@/services/selectionProcesses';
import { buildRegistrationSchema, answerKey } from './validationSchema';
import RegistrationFormFields from './components/RegistrationFormFields';
import { PATHS } from '@/routes/paths';

/* ─── helpers ──────────────────────────────────────────────────────────────── */
function formatDeadline(dateStr) {
  if (!dateStr) return null;
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateStr));
  } catch {
    return null;
  }
}

/* ─── icons ────────────────────────────────────────────────────────────────── */
function CalendarIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4 flex-none"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <rect
        x="3"
        y="4"
        width="18"
        height="18"
        rx="2"
        ry="2"
      />
      <line
        x1="16"
        y1="2"
        x2="16"
        y2="6"
      />
      <line
        x1="8"
        y1="2"
        x2="8"
        y2="6"
      />
      <line
        x1="3"
        y1="10"
        x2="21"
        y2="10"
      />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4 animate-spin"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 18l6-6-6-6"
      />
    </svg>
  );
}

function AlertCircleIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-5 w-5 flex-none text-red-500"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <circle
        cx="12"
        cy="12"
        r="10"
      />
      <path
        strokeLinecap="round"
        d="M12 8v4M12 16h.01"
      />
    </svg>
  );
}

/* ─── loading skeleton ─────────────────────────────────────────────────────── */
function PageSkeleton() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-blue-800 px-6 py-12 sm:py-16">
        <div className="mx-auto max-w-3xl space-y-3">
          <div className="h-5 w-24 animate-pulse rounded-full bg-white/20" />
          <div className="h-8 w-3/4 animate-pulse rounded bg-white/20" />
          <div className="h-4 w-1/2 animate-pulse rounded bg-white/20" />
        </div>
      </div>
      <div className="relative mx-auto -mt-8 max-w-3xl px-4 pb-16 sm:px-6">
        <div className="space-y-4 rounded-xl bg-white p-8 shadow-lg ring-1 ring-slate-200/50">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="h-10 animate-pulse rounded-lg bg-slate-100"
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── load error state ─────────────────────────────────────────────────────── */
function LoadErrorState() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="max-w-sm text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
          <svg
            aria-hidden="true"
            className="h-8 w-8 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
            />
          </svg>
        </div>
        <h1 className="mt-4 text-lg font-semibold text-slate-900">Não foi possível carregar o seletivo</h1>
        <p className="mt-2 text-sm text-slate-500">Verifique sua conexão e tente novamente, ou entre em contato com sua regional.</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-6 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Tentar novamente
        </button>
      </div>
    </div>
  );
}

/* ─── main component ───────────────────────────────────────────────────────── */
export default function PublicRegistrationForm() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [process, setProcess] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    selectionProcessesPublic
      .getBySlug(slug)
      .then(res => setProcess(res.data))
      .catch(err => {
        if ([404, 410].includes(err?.response?.status)) {
          navigate(PATHS.SELETIVO.PUBLIC_CLOSED(slug));
        } else {
          setLoadError(err?.response?.data?.errors || 'Erro ao carregar.');
        }
      });
  }, [slug, navigate]);

  if (loadError) return <LoadErrorState />;
  if (!process) return <PageSkeleton />;

  const attrs = process.attributes || {};
  const deadline = formatDeadline(attrs.ends_at || attrs.registration_ends_at);
  const questions = Array.isArray(attrs.questions) ? attrs.questions : [];
  const targetAudience = attrs.target_audience || 'student';
  const collectEducationInfo = attrs.collect_education_info !== false;

  const initialAnswers = questions.reduce((acc, q) => {
    acc[answerKey(q)] = q.kind === 'checkbox' ? [] : '';
    return acc;
  }, {});

  const initial = {
    full_name: '',
    cpf: '',
    birth_date: '',
    email: '',
    phone: '',
    regional_id: '',
    institution_id: '',
    current_education_level: '',
    desired_grade: '',
    preferred_shift: '',
    special_needs: false,
    special_needs_description: '',
    lgpd_accepted: true,
    honeypot: '',
    answers: initialAnswers
  };

  const hasFileAnswer = answersMap => questions.some(q => q.kind === 'file' && answersMap?.[answerKey(q)] instanceof File);

  const buildAnswersAttributes = answersMap =>
    questions.map(q => {
      const raw = answersMap?.[answerKey(q)];
      if (q.kind === 'checkbox') {
        return { selection_process_question_id: q.id, value_array: Array.isArray(raw) ? raw : [] };
      }
      if (q.kind === 'file') {
        return { selection_process_question_id: q.id, file: raw instanceof File ? raw : null };
      }
      return { selection_process_question_id: q.id, value: raw == null ? '' : String(raw) };
    });

  const buildFormDataPayload = (rest, answersMap) => {
    const fd = new FormData();
    Object.entries(rest).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      fd.append(`registration[${k}]`, typeof v === 'boolean' ? String(v) : v);
    });
    questions.forEach((q, i) => {
      const raw = answersMap?.[answerKey(q)];
      fd.append(`registration[answers_attributes][${i}][selection_process_question_id]`, q.id);
      if (q.kind === 'checkbox') {
        const arr = Array.isArray(raw) ? raw : [];
        if (arr.length === 0) {
          fd.append(`registration[answers_attributes][${i}][value_array][]`, '');
        } else {
          arr.forEach(v => fd.append(`registration[answers_attributes][${i}][value_array][]`, v));
        }
      } else if (q.kind === 'file') {
        if (raw instanceof File) {
          fd.append(`registration[answers_attributes][${i}][file]`, raw);
        }
      } else {
        fd.append(`registration[answers_attributes][${i}][value]`, raw == null ? '' : String(raw));
      }
    });
    return fd;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ── Hero ── */}
      <header className="bg-gradient-to-br from-slate-900 via-blue-900 to-blue-800">
        <div className="mx-auto max-w-3xl px-6 py-12 sm:py-16">
          <div className="mb-10 flex items-center gap-4">
            {brandLogo && (
              <img
                src={brandLogo}
                alt={appName}
                className="h-24 w-auto rounded-md bg-white/95 px-4 py-3 shadow-sm"
              />
            )}
            {/* status badge */}
            <div className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-green-500/20 px-3 py-1 text-xs font-semibold text-green-300 ring-1 ring-green-400/30">
              <span
                className="h-1.5 w-1.5 rounded-full bg-green-400"
                aria-hidden="true"
              />
              Inscrições abertas
            </div>
          </div>

          <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">{attrs.name}</h1>

          {attrs.description && <p className="mt-3 text-lg leading-relaxed text-blue-100">{attrs.description}</p>}

          {deadline && (
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm text-blue-100 ring-1 ring-white/10">
              <CalendarIcon />
              <span>
                Inscrições até <strong className="font-semibold text-white">{deadline}</strong>
              </span>
            </div>
          )}
        </div>
      </header>

      {/* ── Form card ── */}
      <div className="relative mx-auto -mt-8 max-w-3xl px-4 pb-6 sm:px-6">
        <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-slate-200/50">
          <Formik
            initialValues={initial}
            validationSchema={buildRegistrationSchema(questions, targetAudience, collectEducationInfo)}
            onSubmit={async (values, { setSubmitting, setErrors }) => {
              setSubmitError(null);
              try {
                const { answers, ...rest } = values;
                const payload = hasFileAnswer(answers) ? buildFormDataPayload(rest, answers) : { ...rest, answers_attributes: buildAnswersAttributes(answers) };
                const res = await selectionProcessesPublic.submitRegistration(slug, payload);
                navigate(PATHS.SELETIVO.PUBLIC_SUCCESS(slug), {
                  state: { protocol: res.data?.protocol }
                });
              } catch (err) {
                const errors422 = err?.response?.data?.errors;
                if (Array.isArray(errors422)) {
                  const fieldErrors = {};
                  errors422.forEach(e => {
                    const ptr = e?.source?.pointer || '';
                    const m = ptr.match(/\/data\/attributes\/(\w+)/);
                    if (m) fieldErrors[m[1]] = e.detail;
                  });
                  setErrors(fieldErrors);
                }
                setSubmitError('Verifique os campos destacados acima e tente novamente.');
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values, setFieldValue, isSubmitting }) => (
              <Form noValidate>
                <RegistrationFormFields
                  slug={slug}
                  values={values}
                  setFieldValue={setFieldValue}
                  questions={questions}
                  targetAudience={targetAudience}
                  collectEducationInfo={collectEducationInfo}
                />

                {/* ── form footer ── */}
                <div className="border-t border-slate-200 bg-slate-50 px-6 py-6 sm:px-8 sm:py-7">
                  {submitError && (
                    <div
                      role="alert"
                      className="mb-5 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
                    >
                      <AlertCircleIcon />
                      <span>
                        <strong className="font-semibold">Erro ao enviar.</strong> {submitError}
                      </span>
                    </div>
                  )}
                  <div className="flex flex-col-reverse gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-slate-500">Ao enviar, você confirma que os dados informados são verdadeiros e concorda com o tratamento dos seus dados conforme a LGPD.</p>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-blue-600 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                    >
                      {isSubmitting && <SpinnerIcon />}
                      {isSubmitting ? 'Enviando...' : 'Enviar inscrição'}
                      {!isSubmitting && <ArrowRightIcon />}
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      {/* ── trust footer ── */}
      <footer className="mx-auto max-w-3xl px-4 pb-12 pt-4 text-center sm:px-6">
        <p className="text-sm text-slate-500">Dúvidas sobre o processo seletivo? Entre em contato com sua regional.</p>
      </footer>
    </div>
  );
}
